// RUN-EFFORT: Setup › Model policy › Effort tiers (owners and admins). Each run works at a tier —
// low, medium, high, or vision for work with images — and each tier is a gateway model and a
// reasoning level (`GET`/`PUT …/run-effort`). A tier row saves at once; the classifier switch
// too; per-agent overrides (a DE's or a helper's own default effort and tier models) are added and
// edited in a modal, removed from the row's hover icon. Members never see this page.
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  SettingsSection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToggleRow,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GaugeIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import {
  type AgentOverride,
  type EffortAgent,
  type EffortTier,
  effortBodyOf,
  type Reasoning,
  type RunEffortPut,
  type RunEffortView,
  runEffortKey,
  runEffortQuery,
} from "./queries";

const TIER_LABEL: Record<EffortTier, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  vision: "Vision",
};
const TIER_INFO: Record<EffortTier, string> = {
  low: "Routine, short work: upkeep, status, small replies.",
  medium: "Ordinary work: drafts, summaries, short plans.",
  high: "Hard work: analysis, careful decisions, long documents.",
  vision: "Work with images. Picked whenever the work has images.",
};
const REASONING_LABEL: Record<Reasoning, string> = {
  off: "Off",
  low: "Low",
  medium: "Medium",
  high: "High",
};
const TIERS: readonly EffortTier[] = ["low", "medium", "high", "vision"];
/** An agent's own model for a tier keeps the tier's usual reasoning. */
const TIER_REASONING: Record<EffortTier, Reasoning> = {
  low: "off",
  medium: "medium",
  high: "high",
  vision: "medium",
};
const DEFAULT_LABEL = { auto: "Auto", low: "Low", medium: "Medium", high: "High" } as const;
type DefaultEffort = keyof typeof DEFAULT_LABEL;

export function EffortSection({
  api,
  company,
  models,
}: {
  api: Api;
  company: CompanyAccess;
  /** The gateway's allowed models (empty when it lists none: aliases are typed). */
  models: string[];
}) {
  const q = useQuery(runEffortQuery(api, company.id));
  return (
    <SettingsSection
      id="effort"
      icon={GaugeIcon}
      title="Effort tiers"
      description="The model and reasoning each effort level runs on."
      actions={
        <InfoTip label="About effort tiers">
          A DE picks low, medium or high when it hands work on, or leaves it to auto: auto reads the
          task's kind and size and the agent's default, and picks vision when the work has images.
        </InfoTip>
      }
    >
      {q.isPending ? (
        <Loading label="Loading the effort tiers" rows={4} />
      ) : q.isError ? (
        <LoadError what="The effort tiers" error={q.error} />
      ) : (
        <EffortBody api={api} company={company} view={q.data} models={models} />
      )}
    </SettingsSection>
  );
}

function EffortBody({
  api,
  company,
  view,
  models,
}: {
  api: Api;
  company: CompanyAccess;
  view: RunEffortView;
  models: string[];
}) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (body: RunEffortPut) =>
      api.client.runEffort.put({ params: { co: company.id }, body }),
    onSuccess: (v) => qc.setQueryData(runEffortKey(company.id), v),
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  const stored = effortBodyOf(view);
  const setTier = (tier: EffortTier, model: string, reasoning: Reasoning) => {
    const d = view.defaults.find((x) => x.tier === tier);
    const tiers = { ...(stored.tiers ?? {}) };
    if (d && d.model === model && d.reasoning === reasoning) delete tiers[tier];
    else tiers[tier] = { model, reasoning };
    save.mutate({ ...stored, tiers });
  };
  const [editing, setEditing] = useState<EffortAgent | "new" | null>(null);
  const withOverride = view.agents.filter((a) => a.override);
  const setOverride = (id: string, o: AgentOverride | null) => {
    const forAgent = { ...(stored.for_agent ?? {}) };
    if (o) forAgent[id] = o;
    else delete forAgent[id];
    return save.mutateAsync({ ...stored, for_agent: forAgent });
  };
  return (
    <div className="flex flex-col gap-5" data-slot="effort-tiers">
      <Table className={DENSE_TABLE} aria-label="Effort tiers">
        <TableHeader>
          <TableRow>
            <TableHead>Tier</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Reasoning</TableHead>
            <TableHead className="text-right max-sm:hidden">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {view.tiers.map((t) => (
            <TableRow key={t.tier} data-tier={t.tier}>
              <TableCell className="font-medium">
                <span className="flex items-center gap-1">
                  {TIER_LABEL[t.tier]}
                  <InfoTip label={`About the ${t.tier} tier`}>{TIER_INFO[t.tier]}</InfoTip>
                </span>
              </TableCell>
              <TableCell>
                <ModelField
                  label={`${TIER_LABEL[t.tier]} model`}
                  value={t.model}
                  models={models}
                  disabled={save.isPending}
                  onChange={(m) => setTier(t.tier, m, t.reasoning)}
                />
              </TableCell>
              <TableCell>
                <ReasoningSelect
                  label={`${TIER_LABEL[t.tier]} reasoning`}
                  value={t.reasoning}
                  disabled={save.isPending}
                  onChange={(r) => setTier(t.tier, t.model, r)}
                />
              </TableCell>
              <TableCell className="text-right max-sm:hidden">
                {t.routable === false ? (
                  <Badge variant="warning" dot>
                    Not on gateway
                  </Badge>
                ) : t.custom ? (
                  <Badge variant="outline">Custom</Badge>
                ) : (
                  <Badge variant="neutral">Default</Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ToggleRow
        label="Classifier refines auto"
        description="Let the decide classifier move an automatic pick by what the work asks."
        checked={view.refine_with_classifier}
        disabled={save.isPending}
        onCheckedChange={(on) =>
          save.mutate({
            ...stored,
            ...(on ? { refine_with_classifier: true } : { refine_with_classifier: false }),
          })
        }
      />
      <div className="flex flex-col gap-2 border-t border-subtle pt-4">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 text-sm font-medium">
            Per-agent
            <InfoTip label="About per-agent effort">
              A DE (or helper, AG) can have its own default effort and its own model for a tier.
            </InfoTip>
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setEditing("new")}
            disabled={view.agents.length === withOverride.length}
          >
            <PlusIcon aria-hidden={true} data-icon="inline-start" />
            Add override
          </Button>
        </div>
        {withOverride.length === 0 ? (
          <EmptyState
            variant="card"
            icon={GaugeIcon}
            title="No agent overrides"
            description="Every agent uses the tiers above."
          />
        ) : (
          <Table className={DENSE_TABLE} aria-label="Per-agent effort" data-slot="effort-agents">
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="max-sm:hidden">Own tiers</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withOverride.map((a) => (
                <TableRow key={a.id} className="group/row">
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-1.5">
                      <Badge variant="outline">{a.kind === "helper" ? "AG" : "DE"}</Badge>
                      {a.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    {DEFAULT_LABEL[(a.override?.default ?? "auto") as DefaultEffort]}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:hidden">
                    {Object.keys(a.override?.tiers ?? {})
                      .map((t) => TIER_LABEL[t as EffortTier])
                      .join(", ") || "—"}
                  </TableCell>
                  <TableCell>
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${a.name}'s effort`}
                        onClick={() => setEditing(a)}
                      />
                      <RowAction
                        icon={Trash2Icon}
                        label={`Remove ${a.name}'s effort override`}
                        disabled={save.isPending}
                        onClick={() => void setOverride(a.id, null).catch(() => undefined)}
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {view.truncated ? (
          <p className="text-xs text-muted-foreground">Showing the first 500 agents.</p>
        ) : null}
      </div>
      {editing ? (
        <OverrideDialog
          agents={editing === "new" ? view.agents.filter((a) => !a.override) : [editing]}
          initial={editing === "new" ? null : editing}
          models={models}
          pending={save.isPending}
          error={save.isError ? problemText(save.error, "Not saved.") : null}
          onClose={() => {
            save.reset();
            setEditing(null);
          }}
          onSave={async (id, o) => {
            await setOverride(id, o);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function ModelField({
  label,
  value,
  models,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  models: string[];
  disabled?: boolean;
  onChange: (m: string) => void;
}) {
  const [typed, setTyped] = useState(value);
  if (models.length === 0)
    return (
      <Input
        aria-label={label}
        value={typed}
        disabled={disabled}
        className="h-8 font-mono text-xs"
        onChange={(e) => setTyped(e.target.value)}
        onBlur={() => typed.trim() && typed.trim() !== value && onChange(typed.trim())}
      />
    );
  const all = models.includes(value) ? models : [value, ...models];
  return (
    <NativeSelect
      aria-label={label}
      value={value}
      disabled={disabled}
      className="w-full font-mono text-xs"
      onChange={(e) => onChange(e.target.value)}
    >
      {all.map((m) => (
        <NativeSelectOption key={m} value={m}>
          {models.includes(m) ? m : `${m} (not on gateway)`}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

function ReasoningSelect({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: Reasoning;
  disabled?: boolean;
  onChange: (r: Reasoning) => void;
}) {
  return (
    <NativeSelect
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as Reasoning)}
    >
      {(Object.keys(REASONING_LABEL) as Reasoning[]).map((r) => (
        <NativeSelectOption key={r} value={r}>
          {REASONING_LABEL[r]}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

type TierDraft = { [K in EffortTier]?: { model: string; reasoning: Reasoning } | undefined };

function OverrideDialog({
  agents,
  initial,
  models,
  pending,
  error,
  onClose,
  onSave,
}: {
  agents: EffortAgent[];
  initial: EffortAgent | null;
  models: string[];
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (id: string, o: AgentOverride | null) => Promise<void>;
}) {
  const id = useId();
  const [agent, setAgent] = useState(initial?.id ?? agents[0]?.id ?? "");
  const [def, setDef] = useState<DefaultEffort>(
    (initial?.override?.default ?? "auto") as DefaultEffort,
  );
  const [tiers, setTiers] = useState<TierDraft>({ ...(initial?.override?.tiers ?? {}) });
  const submit = () => {
    const own = Object.fromEntries(
      Object.entries(tiers).filter(([, v]) => v?.model.trim()),
    ) as TierDraft;
    const o = {
      ...(def !== "auto" ? { default: def } : {}),
      ...(Object.keys(own).length ? { tiers: own } : {}),
    } as AgentOverride;
    void onSave(agent, Object.keys(o).length ? o : null).catch(() => undefined);
  };
  return (
    <QuickAddDialog
      title={initial ? `Effort for ${initial.name}` : "Agent effort"}
      info="The agent's default effort when nobody asks for one, and its own model for any tier. Empty tiers use the company's."
      submitLabel="Save"
      pending={pending}
      canSubmit={!!agent}
      onSubmit={submit}
      onClose={onClose}
      error={error ? <p className="text-sm text-danger">{error}</p> : null}
      slot="effort-override-dialog"
    >
      {initial ? null : (
        <Field>
          <FieldLabel htmlFor={`${id}-agent`}>Agent</FieldLabel>
          <NativeSelect
            id={`${id}-agent`}
            value={agent}
            onChange={(e) => setAgent(e.target.value)}
            className="w-full"
          >
            {agents.map((a) => (
              <NativeSelectOption key={a.id} value={a.id}>
                {`${a.kind === "helper" ? "AG" : "DE"} · ${a.name}`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      )}
      <Field>
        <FieldLabel htmlFor={`${id}-default`}>Default effort</FieldLabel>
        <NativeSelect
          id={`${id}-default`}
          value={def}
          onChange={(e) => setDef(e.target.value as DefaultEffort)}
        >
          {(Object.keys(DEFAULT_LABEL) as DefaultEffort[]).map((k) => (
            <NativeSelectOption key={k} value={k}>
              {DEFAULT_LABEL[k]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        {TIERS.map((t) => (
          <Field key={t}>
            <FieldLabel htmlFor={`${id}-${t}`}>{`${TIER_LABEL[t]} model`}</FieldLabel>
            {models.length ? (
              <NativeSelect
                id={`${id}-${t}`}
                value={tiers[t]?.model ?? ""}
                className="w-full font-mono text-xs"
                onChange={(e) =>
                  setTiers((x) => ({
                    ...x,
                    [t]: e.target.value
                      ? { model: e.target.value, reasoning: x[t]?.reasoning ?? TIER_REASONING[t] }
                      : undefined,
                  }))
                }
              >
                <NativeSelectOption value="">Company's</NativeSelectOption>
                {models.map((m) => (
                  <NativeSelectOption key={m} value={m}>
                    {m}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            ) : (
              <Input
                id={`${id}-${t}`}
                value={tiers[t]?.model ?? ""}
                placeholder="Company's"
                className="font-mono text-xs"
                onChange={(e) =>
                  setTiers((x) => ({
                    ...x,
                    [t]: e.target.value
                      ? { model: e.target.value, reasoning: x[t]?.reasoning ?? TIER_REASONING[t] }
                      : undefined,
                  }))
                }
              />
            )}
          </Field>
        ))}
      </div>
    </QuickAddDialog>
  );
}
